// Parsing of video links (YouTube, Vimeo, X, direct files).
// Single source of truth for provider detection, duplicate keys and embeds.

export type VideoProvider = "youtube" | "vimeo" | "x" | "linkedin" | "file" | "unknown";

export interface ParsedVideoSource {
  provider: VideoProvider;
  /** Provider-side id (YouTube id, Vimeo id, tweet id). */
  id: string | null;
  /** Stable key used to detect duplicates, e.g. "youtube:dQw4w9WgXcQ". */
  sourceKey: string;
  /** Normalised URL, without tracking params. */
  url: string;
}

const YOUTUBE_RE =
  /^(?:https?:)?\/\/(?:www\.|m\.)?(?:youtu\.be\/|youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|v\/|shorts\/|live\/))([A-Za-z0-9_-]{11})/i;
const VIMEO_RE =
  /^(?:https?:)?\/\/(?:www\.)?(?:player\.)?vimeo\.com\/(?:video\/|channels\/[^/]+\/|groups\/[^/]+\/videos\/|showcase\/\d+\/video\/)?(\d{6,12})(?:$|[/?#])/i;
const X_RE =
  /^(?:https?:)?\/\/(?:www\.|mobile\.)?(?:x|twitter)\.com\/[^/]+\/status\/(\d{5,25})/i;
// Embed URLs (…/embed/feed/update/urn:li:ugcPost:ID) and post URLs
// (…/feed/update/urn:li:activity:ID, …/posts/name-activity-ID-xyz).
const LINKEDIN_URN_RE = /linkedin\.com\/.*urn:li:(ugcPost|share|activity):(\d{10,25})/i;
const LINKEDIN_POST_RE = /linkedin\.com\/posts\/.*-(activity|ugcPost|share)-(\d{10,25})/i;
const FILE_RE = /\.(mp4|webm|mov|m4v)(?:$|[?#])/i;

/** Extracts the src of an <iframe> embed code, or returns the input trimmed. */
export function extractUrlFromInput(input: string): string {
  const match = input.match(/<iframe[^>]*src=["']([^"']+)["'][^>]*>/i);
  return (match ? match[1] : input).trim();
}

function withProtocol(url: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("//")) return `https:${url}`;
  return `https://${url}`;
}

export function parseVideoSource(rawInput: string): ParsedVideoSource {
  const url = withProtocol(extractUrlFromInput(rawInput));

  const yt = url.match(YOUTUBE_RE);
  if (yt) {
    return {
      provider: "youtube",
      id: yt[1],
      sourceKey: `youtube:${yt[1]}`,
      url: `https://www.youtube.com/watch?v=${yt[1]}`,
    };
  }
  const vimeo = url.match(VIMEO_RE);
  if (vimeo) {
    // Unlisted videos need their privacy hash to play: keep it in the URL,
    // but not in the duplicate key.
    const hash = vimeoHash(url, vimeo[1]);
    return {
      provider: "vimeo",
      id: vimeo[1],
      sourceKey: `vimeo:${vimeo[1]}`,
      url: `https://vimeo.com/${vimeo[1]}${hash ? `/${hash}` : ""}`,
    };
  }
  const x = url.match(X_RE);
  if (x) {
    return { provider: "x", id: x[1], sourceKey: `x:${x[1]}`, url };
  }
  const li = url.match(LINKEDIN_URN_RE) ?? url.match(LINKEDIN_POST_RE);
  if (li) {
    return {
      provider: "linkedin",
      id: `${li[1]}:${li[2]}`,
      sourceKey: `linkedin:${li[2]}`,
      url: `https://www.linkedin.com/embed/feed/update/urn:li:${li[1]}:${li[2]}`,
    };
  }
  if (FILE_RE.test(url)) {
    return { provider: "file", id: null, sourceKey: `url:${stripQuery(url)}`, url };
  }
  return { provider: "unknown", id: null, sourceKey: `url:${stripQuery(url)}`, url };
}

function vimeoHash(url: string, id: string): string | null {
  try {
    const u = new URL(url);
    const fromQuery = u.searchParams.get("h");
    if (fromQuery && /^[A-Za-z0-9]{6,32}$/.test(fromQuery)) return fromQuery;
    const m = u.pathname.match(new RegExp(`/${id}/([A-Za-z0-9]{6,32})(?:/|$)`));
    return m ? m[1] : null;
  } catch {
    return null;
  }
}

function stripQuery(url: string): string {
  try {
    const u = new URL(url);
    return `${u.hostname.replace(/^www\./, "")}${u.pathname.replace(/\/$/, "")}`.toLowerCase();
  } catch {
    return url.toLowerCase();
  }
}

export function isValidVideoInput(rawInput: string): boolean {
  const trimmed = rawInput.trim();
  if (!trimmed) return false;
  try {
    new URL(withProtocol(extractUrlFromInput(trimmed)));
    return true;
  } catch {
    return false;
  }
}

export function youtubeThumbnail(
  id: string,
  size: "hqdefault" | "maxresdefault" | "sddefault" | "mqdefault" = "hqdefault",
): string {
  return `https://img.youtube.com/vi/${id}/${size}.jpg`;
}

/** Best-effort static thumbnail for a link, without any network call. */
export function fallbackThumbnail(videoURL: string | undefined): string | undefined {
  if (!videoURL) return undefined;
  const src = parseVideoSource(videoURL);
  return src.provider === "youtube" && src.id ? youtubeThumbnail(src.id) : undefined;
}

/**
 * URL usable in an <iframe>. X posts have no iframe player: callers must
 * resolve them to an mp4 (see lib/xVideo) and use a <video> element.
 */
export function iframeEmbedURL(videoURL: string): string | null {
  const src = parseVideoSource(videoURL);
  if (src.provider === "youtube") return `https://www.youtube.com/embed/${src.id}`;
  if (src.provider === "vimeo") {
    const hash = src.url.split("/")[4];
    return `https://player.vimeo.com/video/${src.id}${hash ? `?h=${hash}` : ""}`;
  }
  if (src.provider === "linkedin") return src.url;
  if (src.provider === "unknown") return src.url;
  return null;
}
